import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Input,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography,
  FormControl,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import {
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import {
  BooleanExplain,
  JobDescription,
  StreetAddress,
  Money,
  Currency,
  CompanyAddressType,
  ForeignNational,
  BooleanTwoPart,
  CountryCode,
  I140,
  J1J2,
  ShippingPreference,
  ShippingPaymentPreference,
  FileData,
  EmploymentBasedCategory,
  CurrencyPeriod,
  BooleanThreePart,
  CompanyNameShowToFN,
} from 'API';
import { Picture, S3Level, S3Object } from 'models';
import FileUpload from 'components/FileUpload';
import DatePicker from 'components/forms/DatePicker';
import S3Image from 'components/S3Image';
import { intComma } from 'humanize-plus';
import {
  useState, useContext, useEffect,
} from 'react';
import CompanyService from 'services/company';
import FormService from 'services/form';
import PictureService from 'services/picture';
import ProcessService from 'services/process';
import StorageService, {
  LOGO_PATH,
  STORAGE_REGION,
  STORAGE_BUCKET,
} from 'services/storage';
import {
  DICTIONARY, FORM_MAX_LENGTH, MONEY_MAX_LENGTH, RECEIPT,
} from 'shared/constants';
import CompanyContext from 'shared/contexts/CompanyContext';
import ProfileContext from 'shared/contexts/ProfileContext';
import { resolvers } from 'shared/enums';
import { Updater } from 'use-immer';
import ProcessContext from 'shared/contexts/ProcessContext';
import UserContext from 'shared/contexts/UserContext';
import { FIRM_COMPANY } from 'services/firm';
import { FIELDS_ENUM, capitalizeString, resolveIsRequired } from 'shared/utilities';
import JobDescriptionContext from 'shared/contexts/JobDescriptionContext';
import { addYears, getYear } from 'date-fns';
import type { AppDateValue } from 'shared/datePickerTypes';
import CurrencyInput from './CurrencyInput';
import GeoPicker from './GeoPicker';
import StreetAddressEditor from './StreetAddress';
import StringArrayInput from './StringArray';
import InputMasked from './InputMasked';

const { saveFile } = StorageService;

const updateValue = (
  updater: Updater<EditableCompany | EditableJobDescription | EditablePerson>,
  field: ToggleableField | ToggleableCheckboxOption,
  value: any,
) => {
  if (!field.fieldName) {
    return;
  }

  updater((draft) => {
    if (field.subFieldName) {
      if (draft[field.subFieldName]) {
        draft[field.subFieldName][field.fieldName] = value;
      } else {
        draft[field.subFieldName] = {
          [field.fieldName]: value,
        };
      }
    } else {
      draft[field.fieldName!] = value;
    }
    if (field.fieldName === FIELDS_ENUM.FIRST_ENTRY_US) {
      const taxTracking = getYear(addYears(new Date(value as string | number | Date), 5));
      draft[FIELDS_ENUM.TAX_TRACKING_F1J1] = taxTracking;
    }
    if (field.fieldName === 'hasSSN' && value === false) {
      draft.usSSN = '';
    }
  });
};

interface IBooleanThreePart { value: boolean, followPrompt: string | undefined, followPromptSecond: string | undefined, followValue: boolean | null, followValueSecond: boolean | null }

interface FieldProps {
  edits: EditableCompany | EditableJobDescription | EditablePerson;
  field: ToggleableField;
  hasAttemptedSave: boolean;
  parentClasses: Record<string, string>;
  setEdits: Updater<EditableCompany | EditableJobDescription | EditablePerson>;
  value: any;
  fieldData: any;
}

const ToggleableFieldField: React.FC<FieldProps> = ({
  edits,
  field,
  hasAttemptedSave,
  parentClasses,
  setEdits,
  value,
  fieldData,
}: FieldProps) => {
  const [reason, setReason] = useState((value as BooleanExplain)?.reason || '');
  const [company] = useContext(CompanyContext);
  const [booleanThreePart, setBooleanThreePart] = useState<IBooleanThreePart>();
  const [process] = useContext(ProcessContext);
  const [profile] = useContext(ProfileContext);
  const [jobDescription] = useContext(JobDescriptionContext);
  const [jobs, setJobs] = useState<Array<JobDescription>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [userState] = useContext(UserContext);
  const { person } = userState;
  const [moneyString, setMoneyString] = useState((value as Money)?.amount ? (value as Money).amount?.toString() : '');
  const source = company || process || profile || (jobDescription);
  const isFirm = company?.id === FIRM_COMPANY.id;

  useEffect(() => {
    setBooleanThreePart(company?.data?.allowsEmployeesToTelecommute as IBooleanThreePart);
  }, []);

  const isRequired = resolveIsRequired(field, edits);
  const requiredErrorMessage = hasAttemptedSave
    && isRequired
    && (value == null || value.length === 0)
    && (
      <FormHelperText error>{DICTIONARY.RequiredField}</FormHelperText>
    );
  const checkValidity = (fieldValue: any) => {
    if (!field.validator && !isRequired) {
      return;
    }

    if (isRequired
      && (fieldValue == null
        // eslint-disable-next-line no-restricted-globals
        || (typeof fieldValue === 'string' && fieldValue.length === 0))
    ) {
      setValidationError(FormService.errors.requiredField);
    } else if (fieldValue !== null && field.validator) {
      setValidationError(field.validator(fieldValue));
    } else {
      setValidationError('');
    }
  };

  const addressHandler = (name: string, v: StreetAddress) => {
    if (!field.fieldName) {
      return;
    }

    setEdits((draft) => {
      draft[field.fieldName] = v;
    });
  };

  const radioHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    updateValue(setEdits, field, e.target.value);
  };

  const booleanExplainValueHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    e.preventDefault();

    const newValue = {
      value: e.target.value === 'true',
      reason: e.target.value === 'true' ? reason : '',
    };

    updateValue(setEdits, field, newValue);
  };

  const booleanExplainReasonHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    e.preventDefault();
    setReason(e.target.value);
    const newValue = {
      value: true, reason: e.target.value,
    };
    updateValue(setEdits, field, newValue);
  };

  const booleanHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const newValue = e.target.value === 'true';
    updateValue(setEdits, field, newValue);
  };

  const handleRadioClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Check if the clicked radio is already selected
    const clickedValue = (e.target as HTMLInputElement).value;
    if (clickedValue === String(value)) {
      updateValue(setEdits, field, null);
    }
  };

  const booleanTwoPartHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    e.preventDefault();
    let newValue = {};

    if (e.target.name === field.fieldName) {
      // It's the first of two booleans
      newValue = {
        value: e.target.value === 'true',
        followPrompt: (field as ToggleableBooleanExplainField).explainPrompt,
        followValue: null,
      };
    } else {
      // It's the second of two booleans, and the first is true
      newValue = {
        value: true,
        followPrompt: (field as ToggleableBooleanExplainField).explainPrompt,
        followValue: e.target.value === 'true',
      };
    }

    updateValue(setEdits, field, newValue);
  };

  const booleanThreePartHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    e.preventDefault();
    let newValue = {};

    if (e.target.name === field.fieldName) {
      // It's the first of two booleans
      newValue = {
        value: e.target.value === 'true',
        followPrompt: (field as ToggleableBooleanExplainField).explainPrompt,
        followPromptSecond: (field as ToggleableBooleanExplainField).explainPromptSecond,
        followValue: booleanThreePart?.followValue,
        followValueSecond: booleanThreePart?.followValueSecond,
      };
    } else if (e.target.name === 'allowsEmployeesToTelecommute followup') {
      // It's the second of two booleans, and the first is true
      newValue = {
        value: true,
        followPrompt: (field as ToggleableBooleanExplainField).explainPrompt,
        followValue: e.target.value === 'true',
        followPromptSecond: (field as ToggleableBooleanExplainField).explainPromptSecond,
        followValueSecond: booleanThreePart?.followValueSecond,
      };
    } else if (e.target.name === 'allowsEmployeesToTelecommute followupSecond') {
      newValue = {
        value: true,
        followPrompt: (field as ToggleableBooleanExplainField).explainPrompt,
        followValue: booleanThreePart?.followValue,
        followPromptSecond: (field as ToggleableBooleanExplainField).explainPromptSecond,
        followValueSecond: e.target.value === 'true',
      };
    }

    setBooleanThreePart((oldBooleanThreePartValue) => ({
      ...oldBooleanThreePartValue,
      ...newValue as IBooleanThreePart,
    }));
    updateValue(setEdits, field, newValue);
  };

  const companyLogoHandler = async (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();

    if (!e?.currentTarget?.files || e.currentTarget.files.length === 0) {
      return;
    }
    const logoVersion = company?.logo?.version ? company.logo.version + 1 : 1;

    const file = e.currentTarget.files[0];
    setIsLoading(true);

    const level = S3Level.PUBLIC;
    const key = await saveFile(
      file,
      level,
      `${LOGO_PATH}/${logoVersion}/${company?.slug}`,
    );

    if (!key) {
      setIsLoading(false);

      return;
    }

    const original: S3Object = {
      region: STORAGE_REGION,
      bucket: STORAGE_BUCKET,
      key,
      level,
    };

    setEdits((draft) => {
      draft.logo = {
        original,
        thumbnails: [],
        version: logoVersion,
      };
    });
    setIsLoading(false);
  };

  const fileSaveHandler = (file: FileData) => {
    updateValue(setEdits, field, file.id);
  };

  const fileCancelHandler = () => {
    updateValue(setEdits, field, null);
  };

  const integerHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();

    if (e.target.value == null) {
      updateValue(setEdits, field, null);
    }
    let amount = parseInt(e.target.value.replace(/\D/g, ''), 10);

    // eslint-disable-next-line no-restricted-globals
    if (isNaN(amount)) {
      amount = 0;
    }

    updateValue(setEdits, field, amount);
  };

  const salaryHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const inputValue = e.target.value;
    const amount = parseFloat(inputValue.replace(/[^0-9.-]/g, '')) || 0;

    // Determine if the amount is negative
    const isNegativeAmount = inputValue.startsWith('-');

    const currency = {
      currency: ((value as Money)?.currency as Currency) || Currency.USD,
      amount: isNegativeAmount ? -Math.abs(amount) : amount,
      period: (value as Money)?.period || CurrencyPeriod.ANNUAL,
    };

    setMoneyString(inputValue);
    updateValue(setEdits, field, currency);
  };

  const salaryPeriodHandler = (
    e: SelectChangeEvent<any>,
  ) => {
    e.preventDefault();
    const currency = {
      currency: ((value as Money)?.currency as Currency) || Currency.USD,
      amount: (value as Money)?.amount || 0,
      period: e.target.value,
    };

    updateValue(setEdits, field, currency);
  };

  const companyAddressHandler = (name: string, newAddress: StreetAddress) => {
    let companyAddressType: CompanyAddressType | null = null;

    if (name === 'addressPrimary') {
      companyAddressType = CompanyAddressType.HEADQUARTERS;
    }

    if (name === 'addressSecondary') {
      companyAddressType = CompanyAddressType.SECONDARY;
    }

    setEdits((draft) => {
      if (isFirm) {
        draft[name] = newAddress;
      } else {
        draft.data[name] = {
          ...newAddress,
          companyAddressType,
        };
      }
    });
  };

  const countryHandler = (event: any, v: any) => {
    updateValue(setEdits, field, v);
  };

  const criminalHistoryHandler = (
    e: SelectChangeEvent<any>,
  ) => {
    const newValue = e.target.value === 'true';
    const crime = e.target.name as string;
    const { fieldName } = field;

    if (!fieldName) {
      return;
    }

    setEdits((draft) => {
      if (draft[fieldName]) {
        draft[fieldName][crime] = newValue;
      } else {
        draft[fieldName] = {
          [crime]: newValue,
        };
      }
    });
  };

  const dateHandler = (
    pickerDate: AppDateValue,
    pickerValue?: string | null | undefined,
  ) => {
    // If picker value is a valid date, save it to the state
    if (pickerValue && Date.parse(pickerValue)) {
      updateValue(setEdits, field, pickerValue);
    }

    if (!pickerDate && !pickerValue) {
      switch (field.fieldName) {
        case 'nivMaxout':
        case 'visaExpiration':
          updateValue(setEdits, field, null);
          break;
        default:
          updateValue(setEdits, field, null);
      }
    }
  };

  const emailHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    checkValidity(e.target.value);
    updateValue(setEdits, field, e.target.value);
  };

  const i140Handler = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();

    const fieldName = 'hasApprovedI140';

    if (e.target.name === fieldName) {
      setEdits((draft) => {
        if (draft[fieldName]) {
          draft[fieldName].hasApproved = e.target.value === 'true';
        } else {
          draft[fieldName] = { hasApproved: e.target.value === 'true' };
        }
      });
    }
  };

  const i140DateHandler = (
    pickerDate: AppDateValue,
    pickerValue?: string | null | undefined,
  ) => {
    const fieldName = 'hasApprovedI140';

    setEdits((draft) => {
      if (draft[fieldName]) {
        draft[fieldName].priorityDate = pickerValue;
      } else {
        draft[fieldName] = { priorityDate: pickerValue };
      }
    });
  };

  const i140EBHandler = (
    e: SelectChangeEvent<any>,
  ) => {
    const fieldName = 'hasApprovedI140';

    setEdits((draft) => {
      if (draft[fieldName]) {
        draft[fieldName].employmentBasedCategory = e.target.value;
      } else {
        draft[fieldName] = { employmentBasedCategory: e.target.value };
      }
    });
  };

  const petitionerHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fieldName = 'hasApprovedI140';

    setEdits((draft) => {
      if (draft[fieldName]) {
        draft[fieldName].petitionerI140 = e.target.value;
      } else {
        draft[fieldName] = { petitionerI140: e.target.value };
      }
    });
  };

  const j1j2Handler = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setEdits((draft) => {
      if (draft.j1j2) {
        draft.j1j2.hasHad = e.target.value === 'true';
        if (e.target.value === 'false') {
          draft.j1j2.start = null;
          draft.j1j2.end = null;
        }
      } else {
        draft.j1j2 = { hasHad: e.target.value === 'true' };
      }
    });
  };

  const j1j2StartHandler = (
    pickerDate: AppDateValue,
    pickerValue?: string | null | undefined,
  ) => {
    setEdits((draft) => {
      if (draft.j1j2) {
        draft.j1j2.start = pickerValue;
      } else {
        draft.j1j2 = { start: pickerValue };
      }
    });
  };

  const j1j2EndHandler = (
    pickerDate: AppDateValue,
    pickerValue?: string | null | undefined,
  ) => {
    setEdits((draft) => {
      if (draft.j1j2) {
        draft.j1j2.end = pickerValue;
      } else {
        draft.j1j2 = { end: pickerValue };
      }
    });
  };

  const phoneHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    checkValidity(e.target.value);
    updateValue(setEdits, field, e.target.value.replace(/[^-+()0-9 ]/g, ''));
  };

  const processLocationHandler = (
    e: SelectChangeEvent<any>,
  ) => {
    e.preventDefault();
    setEdits((draft) => {
      if (draft.employmentData.location) {
        draft.employmentData.location.type = e.target.value;
      } else if (draft.employmentData) {
        draft.employmentData.location = {
          type: e.target.value,
        };
      } else {
        draft.employmentData = {
          location: {
            type: e.target.value,
          },
        };
      }
    });
  };

  const selectHandler = (
    e: SelectChangeEvent<any>,
  ) => {
    e.preventDefault();
    updateValue(setEdits, field, e.target.value);
  };

  const shippingHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();

    setEdits((draft) => {
      if (draft.data.shippingPreference) {
        draft.data.shippingPreference[e.target.name] = e.target.value;
      } else {
        draft.data.shippingPreference = {
          [e.target.name]: e.target.value,
        };
      }
    });
  };

  const stringHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    checkValidity(e.target.value);
    updateValue(setEdits, field, e.target.value);
  };

  const stringArrayHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
    strings: Array<string>,
  ) => {
    checkValidity(e.target.value);
    updateValue(setEdits, field, strings);
  };

  useEffect(() => {
    let ignore = false;
    const compID = (profile as unknown as ForeignNational)?.companyID;

    const loadJobs = async () => {
      setIsLoading(true);
      const jobsList = await CompanyService.getJobDescriptions(compID!);
      if (!ignore) {
        setIsLoading(false);
        setJobs(jobsList);
      }
    };

    if (field.type === 'job-description' && compID) {
      loadJobs();
    }

    return () => { ignore = true; };
  }, []);

  useEffect(() => {
    if (hasAttemptedSave) {
      checkValidity(value);
    }
  }, [hasAttemptedSave]);

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const shippingPreferenceField = (field: ToggleableFieldBase) => {
    if (person.type === 'Employer') {
      return (
        null
      );
    }

    return (
      <Box sx={{
        flexDirection: 'column',
        margin: '0.5rem 0',
        '&:last-child': { marginBottom: 0 },
        '& > *': { display: 'block' },
      }}>
        <Box component="label" sx={(t) => ({ fontSize: '0.7rem', fontWeight: t.typography.fontWeightMedium })} htmlFor="fedex">
          {DICTIONARY.FedExAccount}
        </Box>
        <Input
          id="fedex"
          type="text"
          name="fedexAccount"
          value={(value as ShippingPreference)?.fedexAccount || ''}
          onChange={shippingHandler}
          inputProps={{ maxLength: field?.maxLength || FORM_MAX_LENGTH }}
        />
      </Box>
    );
  };

  switch (field.type) {
    case 'address': {
      return (
        <Box>
          {hasAttemptedSave && validationError && (
            <FormHelperText error>{validationError}</FormHelperText>
          )}
          <StreetAddressEditor
            onChange={addressHandler}
            name={field.fieldName}
            value={value as StreetAddress | null}
            isUS={field.config?.isUS}
            useCareOf={field.config?.useCareOf}
          />
        </Box>
      );
    }

    case 'checkbox': {
      return (
        <FormGroup>
          {field.options?.map((option) => (
            <FormControlLabel
              key={option.fieldName}
              control={(
                <Checkbox
                  checked={edits[option.fieldName]}
                  onChange={() => {
                    updateValue(setEdits, option, !edits[option.fieldName]);
                  }}
                  name={option.fieldName}
                  color="primary"
                  value={edits[option.fieldName] || ''}
                />
              )}
              label={option.label}
            />
          ))}
        </FormGroup>
      );
    }

    case 'company-address': {
      return (
        <Box>
          {hasAttemptedSave && validationError && (
            <FormHelperText error>{validationError}</FormHelperText>
          )}
          <StreetAddressEditor
            onChange={companyAddressHandler}
            name={field.fieldName}
            value={value as StreetAddress | null}
            isUS={field.config?.isUS as boolean | undefined}
          />

        </Box>
      );
    }

    case 'radio': {
      const data = fieldData?.filter(({ fieldName }: any) => fieldName === 'doingBusinessAs');

      return (

        <FormControl>

          {field.fieldName === 'preferredMailingAddress' && (
            <>
              <RadioGroup
                id={field.fieldName}
                value={value}
                name={field.fieldName}
                onChange={radioHandler}
              >
                <>
                  <FormControlLabel sx={{ width: '100%' }} value={CompanyAddressType.HEADQUARTERS} control={<Radio color="primary" />} label="Main Headquarters" />
                  <FormControlLabel sx={{ width: '100%' }} value={CompanyAddressType.SECONDARY} control={<Radio color="primary" />} label="Additional Company Address" />
                </>
              </RadioGroup>
              {requiredErrorMessage}
            </>
          )}

          {field.fieldName === 'companyNameShowToFN' && (
            <>
              <RadioGroup
                id={field.fieldName}
                value={(value !== CompanyNameShowToFN.LEGAL_NAME_OF_US_COMPANY && !data[0].value) ? CompanyNameShowToFN.LEGAL_NAME_OF_US_COMPANY : value}
                name={field.fieldName}
                onChange={radioHandler}
              >
                <>
                  <FormControlLabel sx={{ width: '100%' }} value={CompanyNameShowToFN.LEGAL_NAME_OF_US_COMPANY} control={<Radio color="primary" />} label="Legal Name of U.S. Company" />
                  <FormControlLabel sx={{ width: '100%' }} value={CompanyNameShowToFN.DOING_BUSINESS_AS} disabled={data && !data[0].value} control={<Radio color="primary" />} label='"Doing Business As" Name of Company' />
                </>
              </RadioGroup>
            </>
          )}

        </FormControl>
      );
    }

    case 'boolean':
    case 'process-spouse':
      return (
        <>
          <RadioGroup
            id={field.fieldName}
            name={field.fieldName}
            value={String(value) || ''}
            onChange={booleanHandler}
            onClick={field.fieldName === 'premiumProcessing' ? handleRadioClick : undefined}
          >
            <FormControlLabel
              value="true"
              control={<Radio color="primary" />}
              label="Yes"
            />
            <FormControlLabel
              value="false"
              control={<Radio color="primary" />}
              label="No"
            />
          </RadioGroup>
          {requiredErrorMessage}
          {field.helperText
            && (
              <FormHelperText>{field.helperText}</FormHelperText>
            )}
        </>
      );

    case 'boolean-explain': {
      return (
        <>
          <RadioGroup
            id={field.fieldName}
            name={field.fieldName}
            value={
              value == null || (value as BooleanExplain)?.value == null
                ? null
                : (value as BooleanExplain)?.value
                  ? 'true'
                  : 'false'
            }
            onChange={booleanExplainValueHandler}
          >
            <FormControlLabel
              value="true"
              control={<Radio color="primary" />}
              label="Yes"
            />
            <FormControlLabel
              value="false"
              control={<Radio color="primary" />}
              label="No"
            />
          </RadioGroup>
          {(value as BooleanExplain)?.value && (
            <Box sx={{ mt: 4 }}>
              <label
                className={parentClasses.label}
                htmlFor={`${field.fieldName}-reason`}
              >
                {field.explainPrompt || DICTIONARY.Explain}
              </label>
              <Input
                sx={{ width: '100%' }}
                id={`${field.fieldName}-reason`}
                name={`${field.fieldName as string}-reason`}
                multiline
                value={reason || ''}
                onChange={booleanExplainReasonHandler}
                inputProps={{ maxLength: field.maxLength || FORM_MAX_LENGTH }}
              />
            </Box>
          )}
          {hasAttemptedSave
            && isRequired
            && (value?.value == null
              || (value.value && (!reason || reason.length === 0)))
            && (
              <FormHelperText error>{DICTIONARY.RequiredField}</FormHelperText>
            )}
        </>
      );
    }

    case 'boolean-two-part':
      return (
        <>
          <RadioGroup
            id={field.fieldName}
            name={field.fieldName}
            value={String((value as BooleanTwoPart)?.value)}
            onChange={booleanTwoPartHandler}
            style={{ display: 'flex' }}
          >
            <FormControlLabel
              value="true"
              control={<Radio color="primary" />}
              label="Yes"
            />
            <FormControlLabel
              value="false"
              control={<Radio color="primary" />}
              label="No"
            />
          </RadioGroup>
          {(value as BooleanTwoPart)?.value && (
            <Box sx={{ mt: 4 }}>
              <label
                className={parentClasses.label}
                htmlFor={`${field.fieldName}-followup`}
              >
                {field.explainPrompt}
              </label>
              <RadioGroup
                id={`${field.fieldName}-followup`}
                name={`${field.fieldName} followup`}
                value={
                  (value as BooleanTwoPart)?.followValue == null
                    ? null
                    : (value as BooleanTwoPart).followValue
                      ? 'true'
                      : 'false'
                }
                onChange={booleanTwoPartHandler}
              >
                <FormControlLabel
                  value="true"
                  control={<Radio color="primary" />}
                  label="Yes"
                />
                <FormControlLabel
                  value="false"
                  control={<Radio color="primary" />}
                  label="No"
                />
              </RadioGroup>
            </Box>
          )}
          {hasAttemptedSave
            && isRequired
            && (value?.value == null || (value.value && value.followValue == null))
            && (
              <FormHelperText error>{DICTIONARY.RequiredField}</FormHelperText>
            )}
        </>
      );

    case 'boolean-three-part':
      return (
        <>
          <RadioGroup
            id={field.fieldName}
            name={field.fieldName}
            value={String((value as BooleanThreePart)?.value)}
            onChange={booleanThreePartHandler}
            style={{ display: 'flex' }}
          >
            <FormControlLabel
              value="true"
              control={<Radio color="primary" />}
              label="Yes"
            />
            <FormControlLabel
              value="false"
              control={<Radio color="primary" />}
              label="No"
            />
          </RadioGroup>
          {(value as BooleanThreePart)?.value && (
            <Box sx={{ mt: 4 }}>
              <label
                className={parentClasses.label}
                htmlFor={`${field.fieldName}-followup`}
              >
                {field.explainPrompt}
              </label>
              <RadioGroup
                id={`${field.fieldName}-followup`}
                name={`${field.fieldName} followup`}
                value={
                  (value as BooleanThreePart)?.followValue == null
                    ? null
                    : (value as BooleanThreePart).followValue
                      ? 'true'
                      : 'false'
                }
                onChange={booleanThreePartHandler}
              >
                <FormControlLabel
                  value="true"
                  control={<Radio color="primary" />}
                  label="Yes"
                />
                <FormControlLabel
                  value="false"
                  control={<Radio color="primary" />}
                  label="No"
                />
              </RadioGroup>
            </Box>
          )}
          {(value as BooleanThreePart)?.value && (
            <Box sx={{ mt: 4 }}>
              <label
                className={parentClasses.label}
                htmlFor={`${field.fieldName}-followupSecond`}
              >
                {field.explainPromptSecond}
              </label>
              <RadioGroup
                id={`${field.fieldName}-followupSecond`}
                name={`${field.fieldName} followupSecond`}
                value={
                  (value as BooleanThreePart)?.followValueSecond == null
                    ? null
                    : (value as BooleanThreePart).followValueSecond
                      ? 'true'
                      : 'false'
                }
                onChange={booleanThreePartHandler}
              >
                <FormControlLabel
                  value="true"
                  control={<Radio color="primary" />}
                  label="Yes"
                />
                <FormControlLabel
                  value="false"
                  control={<Radio color="primary" />}
                  label="No"
                />
              </RadioGroup>
            </Box>
          )}
          {hasAttemptedSave
            && isRequired
            && (value?.value == null || (value.value && value.followValue == null))
            && (
              <FormHelperText error>{DICTIONARY.RequiredField}</FormHelperText>
            )}
        </>
      );

    case 'company':
      // Company should not be editable
      return null;

    case 'company-logo':
      return isLoading ? (
        <CircularProgress />
      ) : (
        <>
          {value && (value as Picture).original?.key && (
            <Box sx={{ maxWidth: '200px', maxHeight: '100px', overflow: 'hidden' }}>
              <S3Image
                alt={`${company?.name} logo`}
                imgkey={
                  PictureService.targetPictureSize(value as Picture, 200).key
                }
              />
            </Box>
          )}
          <input
            accept="image/png, image/jpeg"
            style={{ display: 'none' }}
            id="contained-button-file"
            multiple
            type="file"
            name={field.fieldName}
            onChange={companyLogoHandler}
          />
          <label htmlFor="contained-button-file">
            <Button
              variant="contained"
              color="primary"
              component="span"
              size="small"
              sx={{ mt: 1 }}
              startIcon={<CloudUploadIcon />}
            >
              Upload
            </Button>
          </label>
        </>
      );

    case 'country':
    case 'countries':
      return (
        <>
          <GeoPicker
            multiple={field.type === 'countries'}
            type="country"
            value={(value as CountryCode | Array<CountryCode> | null)}
            onChange={countryHandler}
          />
          {requiredErrorMessage}
          <FormHelperText>
            {field?.helperText ? field.helperText : ''}
          </FormHelperText>
        </>
      );

    case 'criminal-history':
      return (
        <>
          <Box display="flex" sx={{ mt: 2 }}>
            <Box sx={{ width: '48px', textAlign: 'center' }}>No</Box>
            <Box sx={{ width: '48px', textAlign: 'center' }}>Yes</Box>
          </Box>
          {ProcessService.crimeFields.map((crime) => (
            <Box sx={{ display: 'flex', width: '100%' }} key={crime}>
              <Box sx={{ width: '48px', textAlign: 'center' }}>
                <Radio
                  color="primary"
                  value="false"
                  name={crime}
                  onChange={criminalHistoryHandler}
                  checked={
                    value ? (value as any)[crime] === false : false
                  }
                />
              </Box>
              <Box sx={{ width: '48px', textAlign: 'center' }}>
                <Radio
                  value="true"
                  name={crime}
                  color="primary"
                  onChange={criminalHistoryHandler}
                  checked={
                    value ? (value as any)[crime] === true : false
                  }
                />
              </Box>
              <Box flexGrow={1} sx={{ pt: 1 }}>
                <Typography variant="body2">
                  {(DICTIONARY.CriminalHistory as any)[crime]}
                </Typography>
              </Box>
            </Box>
          ))}
          {hasAttemptedSave
            && isRequired
            && (!value || ProcessService.crimeFields.some(
              (crime) => (value as any)[crime] == null,
            ))
            && (
              <FormHelperText error>{DICTIONARY.RequiredField}</FormHelperText>
            )}
        </>
      );

    case 'date':
      return (
        <>
          <DatePicker
            value={(value as string) || null}
            onChange={dateHandler}
            id={field.fieldName}
          />
          {requiredErrorMessage}
        </>
      );

    case 'email':
      return (
        <>
          <Input
            error={hasAttemptedSave ? validationError.length > 0 : false}
            id={field.fieldName}
            name={field.fieldName as string}
            onChange={emailHandler}
            type="text"
            value={value}
            inputProps={{ maxLength: field.maxLength || FORM_MAX_LENGTH }}
          />
          {hasAttemptedSave && validationError && (
            <FormHelperText error>{validationError}</FormHelperText>
          )}
        </>
      );

    case 'enum':
      return (
        <>
          {field.enumPrompt && <Typography>{field.enumPrompt}</Typography>}
          <Select
            id={field.fieldName}
            name={field.fieldName}
            value={value || ''}
            onChange={selectHandler}
            variant={field.config?.selectVariant}
            sx={{ width: '100%' }}
          >
            {(Object
              .values(field.enum) as Array<string>)
              .sort((a: any, b: any) => {
                const resolvedA = field.enumResolver(a);
                const resolvedB = field.enumResolver(b);
                if (resolvedA !== 'Not sure' && resolvedB !== 'Not sure') {
                  return resolvedA < resolvedB ? -1 : 1;
                }

                return 1;
              })
              .map((enumKey) => (
                <MenuItem
                  key={enumKey}
                  value={enumKey}
                  sx={{ fontStyle: enumKey === 'NOT_SURE' ? 'italic' : 'normal' }}
                >
                  {field.enumResolver(enumKey)}
                </MenuItem>
              ))}
          </Select>
          {requiredErrorMessage}
          {field.helperText
            && (
              <FormHelperText>{field.helperText}</FormHelperText>
            )}
        </>
      );

    case 'enum-radio':
      return (
        <>
          {field.enumPrompt && <Typography>{field.enumPrompt}</Typography>}
          <RadioGroup
            id={field.fieldName}
            name={field.fieldName}
            value={value}
            onChange={stringHandler}
          >
            {(Object
              .values(field.enum) as Array<string>)
              .sort((a: any, b: any) => {
                const resolvedA = field.enumResolver(a);
                const resolvedB = field.enumResolver(b);

                return resolvedA < resolvedB ? -1 : 1;
              })
              .map((enumKey) => (
                <FormControlLabel
                  key={`${enumKey}${field.fieldName}`}
                  value={enumKey}
                  control={<Radio color="primary" />}
                  label={field.enumResolver(enumKey)}
                />
              ))}
          </RadioGroup>
          {requiredErrorMessage}
        </>
      );

    case 'file': {
      let file = (source as any)[field.connectionField!];
      if (field.fieldName === 'uploadedDescriptionID' && jobDescription?.uploadedDescription) {
        file = (jobDescription as any)[field.connectionField!];
      }

      let ownership: DocumentOwnership;
      let storage: DocumentStorage;
      if (process) {
        ownership = {
          companyID: process?.companyID || undefined,
          foreignNationalID: process?.foreignNationalID
            || process?.employeeData?.tempID
            || undefined,
        };
        storage = {
          ownerNationalID: process?.foreignNationalID
            || process?.employeeData?.tempID
            || '',
        };
      } else if (profile?.type === 'ForeignNational') {
        ownership = {
          foreignNationalID: profile.id!,
        };
        storage = {
          ownerNationalID: profile.id!,
        };
      } else if (company) {
        ownership = {
          companyID: company.id!,
        };
        storage = {
          companyID: company.id!,
        };
      } else if (jobDescription) {
        ownership = {
          companyID: jobDescription?.companyID || undefined,
          foreignNationalID: jobDescription?.uploadedDescription?.ownerNationalID || person?.id
            || undefined,
        };
        storage = {
          ownerNationalID: jobDescription?.uploadedDescription?.ownerNationalID || person?.id
            || '',
        };
      } else {
        return null;
      }

      return (
        <div>
          <FileUpload
            existingFile={file}
            onSave={fileSaveHandler}
            onCancel={fileCancelHandler}
            fileType={field.config?.fileType}
            ownership={ownership}
            storage={storage}
          />
        </div>
      );
    }

    case 'i140': {
      const options = Object.keys(EmploymentBasedCategory);

      return (
        <>
          <RadioGroup
            id={field.fieldName}
            name={field.fieldName}
            value={
              ((value as I140)?.hasApproved == null)
                ? null
                : (value as I140)?.hasApproved
                  ? 'true'
                  : 'false'
            }
            onChange={i140Handler}
            style={{ display: 'flex' }}
          >
            <FormControlLabel
              value="true"
              control={<Radio color="primary" />}
              label="Yes"
            />
            <FormControlLabel
              value="false"
              control={<Radio color="primary" />}
              label="No"
            />
          </RadioGroup>
          {(value as I140)?.hasApproved && (
            <>
              <Box sx={{ mt: 2 }}>
                <label
                  className={parentClasses.label}
                  htmlFor={`${field.fieldName}-classification`}
                >
                  {DICTIONARY.I140.classificationPrompt}
                </label>
                <Select
                  value={(value as I140).employmentBasedCategory}
                  onChange={i140EBHandler}
                  sx={{ width: '100%' }}
                >
                  {options.map((option) => (
                    <MenuItem key={option} value={option}>
                      {resolvers.EmploymentBasedCategory(
                        option as EmploymentBasedCategory,
                      )}
                    </MenuItem>
                  ))}
                </Select>
              </Box>

              <Box sx={{ mt: 2 }}>
                <label
                  className={parentClasses.label}
                  htmlFor={`${field.fieldName}-date`}
                >
                  {DICTIONARY.I140.priorityDatePrompt}
                </label>

                <DatePicker
                  value={(value as I140).priorityDate || null}
                  onChange={i140DateHandler}
                  id={`${field.fieldName}-date`}
                  name={`${field.fieldName}-date`}
                />
              </Box>

              <Box sx={{ mt: 2 }}>
                <label
                  className={parentClasses.label}
                  htmlFor={`${field.fieldName}`}
                >
                  {DICTIONARY.I140.petitionerI140}
                </label>
                <Input
                  sx={{ width: '100%' }}
                  id={field.fieldName}
                  name={DICTIONARY.I140.petitionerI140}
                  onChange={petitionerHandler}
                  type="text"
                  value={(value as I140).petitionerI140 || null}
                />

              </Box>

            </>
          )}
          {hasAttemptedSave
            && isRequired
            && (value?.value == null)
            && (
              <FormHelperText error>{DICTIONARY.RequiredField}</FormHelperText>
            )}
        </>
      );
    }

    case 'integer':
    case 'process-dependents':

      return (
        <>
          <Input
            id={field.fieldName}
            name={field.fieldName as string}
            type="text"
            value={value !== null ? intComma(value as number) : ''}
            onChange={integerHandler}
            inputProps={{ maxLength: field.maxLength || FORM_MAX_LENGTH }}
          />
          {hasAttemptedSave && validationError && !requiredErrorMessage && (
            <FormHelperText error>{validationError}</FormHelperText>
          )}
          {requiredErrorMessage}
          {field.helperText
            && (
              <FormHelperText>{field.helperText}</FormHelperText>
            )}
        </>
      );

    case 'j1j2':
      return (
        <>
          <RadioGroup
            id={field.fieldName}
            name={field.fieldName}
            value={
              value === null || (value as J1J2)?.hasHad === null
                ? null
                : (value as J1J2)?.hasHad
                  ? 'true'
                  : 'false'
            }
            onChange={j1j2Handler}
            style={{ display: 'flex' }}
          >
            <FormControlLabel
              value="true"
              control={<Radio color="primary" />}
              label="Yes"
            />
            <FormControlLabel
              value="false"
              control={<Radio color="primary" />}
              label="No"
            />
          </RadioGroup>
          {(value as J1J2)?.hasHad && (
            <Box sx={{ display: 'flex', gap: '1rem' }}>
              <Box sx={{ flex: 1 }}>
                <Typography className={parentClasses.label}>Start</Typography>
                <DatePicker
                  value={(value as J1J2).start || null}
                  onChange={j1j2StartHandler}
                  id={`${field.fieldName}-end`}
                  name="j1j2-start"
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography className={parentClasses.label}>End</Typography>
                <DatePicker
                  value={(value as J1J2).end || null}
                  onChange={j1j2EndHandler}
                  id={`${field.fieldName}-end`}
                  name="j1j2-end"
                />
              </Box>
            </Box>
          )}
          {hasAttemptedSave
            && isRequired
            && (value?.hasHad == null || (value.hasHad && !value.start))
            && (
              <FormHelperText error>{DICTIONARY.RequiredField}</FormHelperText>
            )}
        </>
      );

    case 'job-description':
      if (!(profile as unknown as ForeignNational)?.companyID) {
        return (
          <Typography variant="body2">
            This person is not attached to a company.
          </Typography>
        );
      }

      if (isLoading) {
        return null;
      }

      if (jobs.length === 0) {
        return (
          <Typography variant="body2">
            {person.id === profile?.id ? 'Your ' : 'This person’s '}
            company does not have any job descriptions from which to choose.
          </Typography>
        );
      }

      return (
        <Select onChange={selectHandler} value={value} sx={{ width: '100%' }}>
          {jobs.map((job) => (
            <MenuItem key={job.id} value={job.id}>
              {job.title}
            </MenuItem>
          ))}
        </Select>
      );

    case 'manager':
      // Should not be editable here.
      return null;

    case 'money': {
      return (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CurrencyInput id="salary-input" placeholder="$0.00" type="text" onChange={salaryHandler} value={moneyString} />
          </Box>
          {/* This error occur when empty string as a input will save */}
          {hasAttemptedSave && validationError && (
            <FormHelperText error>{validationError}</FormHelperText>
          )}

          {hasAttemptedSave
            && isRequired
            && value?.amount == null
            && !validationError
            && (
              <FormHelperText error>{validationError}</FormHelperText>
            )}
        </>
      );
    }

    case 'salary': {
      // For salaries, we allow cents and give the option to select pay period
      const periods = [
        CurrencyPeriod.ANNUAL,
        CurrencyPeriod.QUARTERLY,
        CurrencyPeriod.MONTHLY,
        CurrencyPeriod.WEEKLY,
        CurrencyPeriod.HOURLY,
      ];

      return (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Box sx={{ minWidth: '2.5rem', fontWeight: 500 }}>
              {/* TODO: Someday this can become a separate dropdown to
                  set the currency to othe than USD, if needed */}
              {resolvers.Currency((value as Money)?.currency as Currency)
                || Currency.USD}
            </Box>
            <Input
              id={field.fieldName}
              name={field.fieldName as string}
              type="text"
              value={moneyString || ''}
              onChange={salaryHandler}
              inputProps={{ maxLength: field.maxLength || MONEY_MAX_LENGTH }}
            />
          </Box>
          <Select
            id={`${field.fieldName}-period`}
            value={(value as Money)?.period || CurrencyPeriod.ANNUAL}
            onChange={salaryPeriodHandler}
            sx={{ width: '100%', mt: 1 }}
            variant="outlined"
          >
            {periods.map((period) => (
              <MenuItem
                key={period}
                value={period}
              >
                {capitalizeString(resolvers.CurrencyPeriod(period))}
              </MenuItem>
            ))}
          </Select>
          {hasAttemptedSave
            && isRequired
            && value?.amount == null
            && (
              <FormHelperText error>{DICTIONARY.RequiredField}</FormHelperText>
            )}
        </>
      );
    }

    case 'multiselect':
      return (
        <>
          <Select
            id={field.fieldName}
            value={value || []}
            onChange={selectHandler}
            multiple
            sx={{ width: '100%' }}
          >
            {(field as ToggleableSelectField).options?.map((option) => (
              <MenuItem key={`${field.fieldName}-${option.label}`} value={option.value || ''}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          {requiredErrorMessage}
        </>
      );

    case 'phone':
      return (
        <>
          <Input
            error={hasAttemptedSave ? validationError.length > 0 : false}
            id={field.fieldName}
            name={field.fieldName as string}
            onChange={phoneHandler}
            type="text"
            value={value || ''}
            inputProps={{ maxLength: field.maxLength || FORM_MAX_LENGTH }}
          />
          {hasAttemptedSave && validationError && (
            <FormHelperText error>{validationError}</FormHelperText>
          )}
          {field.helperText
            && (
              <FormHelperText>{field.helperText}</FormHelperText>
            )}
        </>
      );

    case 'process-location':
      return (
        <>
          <Select
            id={field.fieldName}
            name={field.fieldName}
            value={value?.type || ''}
            onChange={processLocationHandler}
            sx={{ width: '100%' }}
          >
            {(Object.values(field.enum) as Array<string>).map((enumKey) => (
              <MenuItem
                key={enumKey}
                value={enumKey}
              >
                {field.enumResolver(enumKey)}
              </MenuItem>
            ))}
          </Select>
        </>
      );

    case 'select':
      return (
        <>
          <Select
            id={field.fieldName}
            value={(field as ToggleableSelectField)?.options?.find(
              (option) => option.label === value,
            )?.value || value || ''}
            onChange={
              (field as ToggleableSelectField).onChange
                ? undefined : selectHandler
            }
            variant={field.config?.selectVariant}
            sx={{ width: '100%' }}
          >
            {(field as ToggleableSelectField).options?.map((option) => (
              <MenuItem
                key={`${field.fieldName}-${option.label}`}
                value={option.value || ''}
                onClick={
                  (field as ToggleableSelectField).onChange
                  && (
                    () => (field as ToggleableSelectField)!.onChange!(option.value))
                }
              >
                {option.label}
              </MenuItem>
            ))}
          </Select>
          {requiredErrorMessage}
        </>
      );

    case 'shipping':
      return (
        <>
          <RadioGroup
            id={`${field.fieldName}`}
            name="preference"
            value={
              value
                ? ((value as ShippingPreference)
                  .preference as ShippingPaymentPreference)
                : {}
            }
            onChange={shippingHandler}
          >
            {(Object.values(
              ShippingPaymentPreference,
            ) as Array<ShippingPaymentPreference>).map((enumKey) => (
              <FormControlLabel
                key={enumKey}
                value={enumKey}
                control={<Radio color="primary" />}
                label={resolvers.ShippingPaymentPreference(enumKey)}
              />
            ))}
          </RadioGroup>
          {(value as ShippingPreference)?.preference
              === ShippingPaymentPreference.FEDEX && (
            shippingPreferenceField(field)
          )}
        </>
      );

    case 'string':
    case 'prevailing-wage-case-number':
    case 'process-firstname':
    case 'process-lastname':
    case 'process-email':

      if (field.fieldName === RECEIPT.SELECTION_NOTICE_NO) {
        return (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <InputMasked id="receipt-input" mask="****-****-****-****" onChange={stringHandler} value={value || ''} />
            </Box>

            {hasAttemptedSave && validationError && (
              <FormHelperText error>{validationError}</FormHelperText>
            )}

            {field.helperText
              && (
                <FormHelperText>{field.helperText}</FormHelperText>
              )}
          </>
        );
      }

      return (
        <>
          <Input
            error={hasAttemptedSave ? validationError.length > 0 : false}
            id={field.fieldName}
            multiline={Boolean(field.maxLength && field.maxLength > 50)}
            name={field.fieldName as string}
            onChange={stringHandler}
            type="text"
            value={value || ''}
            inputProps={{ maxLength: field.maxLength || FORM_MAX_LENGTH }}
          />
          {hasAttemptedSave && validationError && (
            <FormHelperText error>{validationError}</FormHelperText>
          )}
          {field.helperText
            && (
              <FormHelperText>{field.helperText}</FormHelperText>
            )}
        </>
      );

    case 'stringArray':
      return (
        <>
          <StringArrayInput
            error={hasAttemptedSave ? validationError.length > 0 : false}
            value={value}
            handler={stringArrayHandler}
            id={field.fieldName}
            name={field.fieldName as string}
          />
          {hasAttemptedSave && validationError && (
            <FormHelperText error>{validationError}</FormHelperText>
          )}
          <FormHelperText>
            {field.helperText ? field.helperText : DICTIONARY.UseCommas}
          </FormHelperText>
        </>
      );

    default:
      return <span>Other</span>;
  }
};

export default ToggleableFieldField;
